import { useCallback, useRef, useState } from "react";
import { clampSize, readSize, writeSize, type PanelSpec } from "../lib/panelSize";

/** A panel size that survives reloads. */
export function usePanelSize(spec: PanelSpec): [number, (v: number) => void] {
  const [size, setSize] = useState(() => readSize(spec));
  const set = useCallback((v: number) => { const c = clampSize(spec, v); setSize(c); writeSize(spec, c); }, [spec]);
  return [size, set];
}

interface Props {
  /** "x" resizes a width (vertical bar), "y" a height (horizontal bar). */
  axis: "x" | "y";
  spec: PanelSpec;
  size: number;
  onSize: (v: number) => void;
  /** -1 when dragging toward the start (left/up) grows the panel, i.e. the panel sits after the bar. */
  sign?: 1 | -1;
  label: string;
  testId?: string;
}

/** Drag bar between two panels. Double-click resets; arrow keys step by 10px (Shift: 50px). */
export function Splitter({ axis, spec, size, onSize, sign = 1, label, testId }: Props) {
  const start = useRef<{ pos: number; size: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const pos = (e: React.PointerEvent) => (axis === "x" ? e.clientX : e.clientY);
  return (
    <div className={`splitter splitter-${axis}`} role="separator" aria-orientation={axis === "x" ? "vertical" : "horizontal"}
      aria-label={label} aria-valuemin={spec.min} aria-valuemax={spec.max} aria-valuenow={size} tabIndex={0}
      data-dragging={dragging || undefined} data-testid={testId} title={`${label} — drag to resize, double-click to reset`}
      onPointerDown={(e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        start.current = { pos: pos(e), size };
        setDragging(true);
      }}
      onPointerMove={(e) => { if (start.current) onSize(start.current.size + sign * (pos(e) - start.current.pos)); }}
      onPointerUp={() => { start.current = null; setDragging(false); }}
      onPointerCancel={() => { start.current = null; setDragging(false); }}
      onDoubleClick={() => onSize(spec.def)}
      onKeyDown={(e) => {
        const step = e.shiftKey ? 50 : 10;
        const keys = axis === "x" ? ["ArrowLeft", "ArrowRight"] : ["ArrowUp", "ArrowDown"];
        const i = keys.indexOf(e.key);
        if (i < 0) return;
        e.preventDefault(); e.stopPropagation();          // arrows here resize; they must not also page images
        onSize(size + sign * (i === 0 ? -step : step));
      }} />
  );
}
